import { FunctionsHttpError } from '@supabase/supabase-js'

import { supabase } from './supabase.helpers'

export type SubmissionOutcome = 'pending' | 'approved' | 'rejected'

export const SUBMISSION_OUTCOMES: SubmissionOutcome[] = ['pending', 'approved', 'rejected']

export type SubmissionsCounters = {
  total: number
  pending: number
  approved: number
  rejected: number
  /** Approved with the title changed in moderation: a SUBSET of approved, not a fifth state. */
  revised: number
}

export type SubmissionEntry = {
  id: string
  originalTitle: string
  titleAtModeration: string | null
  currentTitle: string | null
  outcome: SubmissionOutcome
  submittedAt: string
  moderatedAt: string | null
  spotId: string | null
  submitter: { id: string; name: string | null } | null
  /** null unless approved: for a pending or rejected proposal the question has no answer. */
  revised: boolean | null
}

export type SubmissionsPage = {
  counters: SubmissionsCounters
  submissions: SubmissionEntry[]
  limit: number
  offset: number
}

/**
 * The first pagination of the repo (12.2). "Show more" with a growing offset,
 * not numbered pages: consultation is an owner's analysis, insertions are rare,
 * and a row seen twice in a read-only panel costs nothing. Small enough that the
 * e2e can reach the second page with a handful of fixtures.
 */
export const SUBMISSIONS_PAGE_SIZE = 20

/**
 * The proposal history of an experience (12.1), for the owner only.
 *
 * A 403 is not an error here: the function is gated on `view_submissions`, so
 * the refusal IS the answer — this caller gets the refusal state, not a toast.
 * Any other failure is a real error and comes back as such.
 */
export const getExperienceSubmissionsStats = async ({
  experienceId,
  outcome,
  offset = 0,
  limit = SUBMISSIONS_PAGE_SIZE,
}: {
  experienceId: string
  outcome?: SubmissionOutcome | null
  offset?: number
  limit?: number
}): Promise<{ forbidden: boolean; error: boolean; page: SubmissionsPage | null }> => {
  const { data, error } = await supabase.functions.invoke('get-experience-submissions-stats', {
    body: { experienceId, ...(outcome && { outcome }), limit, offset },
  })

  if (error) {
    const forbidden = error instanceof FunctionsHttpError && error.context?.status === 403
    return { forbidden, error: !forbidden, page: null }
  }

  return { forbidden: false, error: false, page: data as SubmissionsPage }
}
